import React from 'react';
import { message } from 'components';
import {
  getAppointmentSignature,
  getExternalAppointmentSignature,
  getExternalTemplate,
  getTemplate,
  saveAppointmentSignature,
  saveExternalAppointmentSignature,
} from 'api/Covid19Controller';
import errorReporter from '@app/errorReporter';
import type { CovidAppointmentType } from '@app/covid19/COVID19AckTemplate';
import type { Country } from '@bb/domain/dist/types';

const useCovidSign = (dashboardInfo?: {
  dashboardId: string;
  retsId: string;
  country: Country;
}) => {
  const [isWorking, setIsWorking] = React.useState(false);
  const [selectedCovidAppt, setSelectedCovidAppt] =
    React.useState<CovidAppointmentType>(null);
  const [selectedCovidTemplate, setSelectedCovidTemplate] =
    React.useState<string>(null);
  const [selectedCovidSignSettings, setSelectedCovidSignSettings] =
    React.useState<Awaited<ReturnType<typeof getAppointmentSignature>>>(null);

  const fetchTemplate = async () => {
    if (isWorking) return;
    setIsWorking(true);
    try {
      let template: string;
      if (!dashboardInfo) {
        template = await getTemplate({
          listingId: selectedCovidAppt.listingId,
          type: 'ackTemplate',
        });
      } else {
        template = await getExternalTemplate({
          retsId: dashboardInfo.retsId,
          dashboardId: dashboardInfo.dashboardId,
          type: 'ackTemplate',
        });
      }

      if (template) {
        setSelectedCovidTemplate(template);
      }
    } catch (err) {
      errorReporter.error(err);
      message.error('Error fetching Covid template.');
    }
    setIsWorking(false);
  };

  React.useEffect(() => {
    if (
      selectedCovidAppt &&
      !selectedCovidAppt.covid19Signed &&
      !selectedCovidTemplate
    ) {
      fetchTemplate();
    }
  }, [selectedCovidAppt, selectedCovidTemplate, dashboardInfo]);

  React.useEffect(() => {
    let active = true;
    if (
      selectedCovidAppt &&
      selectedCovidAppt.covid19Signed &&
      !selectedCovidSignSettings
    ) {
      setIsWorking(true);
      const fetchSignature = async () => {
        let signature;
        if (!dashboardInfo) {
          signature = await getAppointmentSignature({
            appointmentId: selectedCovidAppt.id,
          });
        } else {
          signature = await getExternalAppointmentSignature({
            appointmentId: selectedCovidAppt.id,
            dashboardInfo,
          });
        }

        if (active) {
          setSelectedCovidSignSettings(signature);
          setIsWorking(false);
        }
      };
      fetchSignature();
    }

    return () => {
      active = false;
    };
  }, [selectedCovidAppt, selectedCovidSignSettings, dashboardInfo]);

  const resetCovidStates = () => {
    setSelectedCovidAppt(null);
    setSelectedCovidTemplate(null);
    setSelectedCovidSignSettings(null);
  };

  const handleCovidSign = async (
    signature: string,
    onComplete: (success: boolean) => void,
  ) => {
    setIsWorking(true);
    try {
      if (!dashboardInfo) {
        await saveAppointmentSignature({
          appointmentId: selectedCovidAppt.id,
          covid19Signed: signature,
          template: selectedCovidTemplate,
        });
      } else {
        await saveExternalAppointmentSignature({
          appointmentId: selectedCovidAppt.id,
          covid19Signed: signature,
          template: selectedCovidTemplate,
          ...dashboardInfo,
        });
      }
      message.success('COVID-19 signed');
      resetCovidStates();
      onComplete(true);
    } catch (err) {
      errorReporter.error(`Error saving signature: ${err}`);
      onComplete(false);
    } finally {
      setIsWorking(false);
    }
  };

  return {
    isWorking,
    setIsWorking,
    selectedCovidAppt,
    setSelectedCovidAppt,
    selectedCovidTemplate,
    setSelectedCovidTemplate,
    selectedCovidSignSettings,
    setSelectedCovidSignSettings,
    resetCovidStates,
    handleCovidSign,
  };
};

export default useCovidSign;
